import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { updateCard } from "../api/cardsApi";
import { Card, CardType } from "../types";
import RichTextEditor from "@/components/ui/RichTextEditor";

const formSchema = z.object({
  content: z.string().min(1, "Content is required"),
  card_type_id: z.string().min(1, "Card type is required"),
});

type FormValues = z.infer<typeof formSchema>;

interface EditCardDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  card: Card | null;
  onSaved?: () => void;
  cardTypes: CardType[];
}

export default function EditCardDialog({ open, onOpenChange, card, onSaved, cardTypes }: EditCardDialogProps) {
  const [submitting, setSubmitting] = React.useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    values: {
      content: card?.content ?? "",
      card_type_id: card?.card_type_id ? String(card.card_type_id) : "",
    },
  });

  React.useEffect(() => {
    if (card) {
      form.reset({
        content: card.content ?? "",
        card_type_id: card.card_type_id ? String(card.card_type_id) : "",
      });
    }
  }, [card, form]);

  async function handleSubmit(values: FormValues) {
    if (!card?.id) return;
    setSubmitting(true);
    try {
      await updateCard(card.id, {
        content: values.content,
        card_type_id: Number(values.card_type_id),
      });
      toast.success("Card updated successfully");
      onSaved?.();
      onOpenChange(false);
    } catch {
      toast.error("Failed to update card");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="!w-[95vw] sm:!w-full !max-w-[600px] sm:!max-w-[900px] !max-h-[95vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Card</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              name="card_type_id"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="after:content-['*'] after:text-red-500 after:ml-1">Card Type</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a card type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {cardTypes.map((ct) => (
                        <SelectItem key={ct.id} value={String(ct.id)}>
                          {ct.type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              name="content"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="after:content-['*'] after:text-red-500 after:ml-1">Content</FormLabel>
                  <FormControl>
                    <RichTextEditor
                      value={field.value || ""}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      placeholder="Enter card content (HTML supported)"
                      minHeight="220px"
                      defaultTextColor="#ffffff"
                      backgroundColor="#1e1e1e"
                      defaultFontSize="20"
                      showImageAndColor
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
